import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { MOST_UPLOADED_DOCUMENT_TYPES } from './constants';

import { Document, DocumentRequest } from 'modules/common/interfaces/documents';
import DocumentType from 'modules/Documents/interfaces/DocumentType.interface';

dayjs.extend(utc);

export const formatFriendlyFullDate = (date: string) => {
    const dayjsDate = dayjs(date);
    const today = dayjs();
    const yesterday = dayjs().subtract(1, 'day');

    if (dayjsDate.isSame(today, 'day')) {
        return dayjsDate.format('[Today at] hh:mm A');
    }

    if (dayjsDate.isSame(yesterday, 'day')) {
        return dayjsDate.format('[Yesterday at] hh:mm A');
    }

    return dayjsDate.format('MMMM D, YYYY [at] hh:mm A');
};

export const getLastXYears = (numYears: number | null, inclusive = true) => {
    let now = dayjs.utc();
    let amountOfYears = numYears;

    if (!amountOfYears) {
        const since = dayjs('2018-01-01').utc();
        amountOfYears = now.diff(since, 'year');
        amountOfYears = inclusive ? amountOfYears + 1 : amountOfYears;
    }

    return Array(amountOfYears)
        .fill(null)
        .map((_, index) => {
            if (!(inclusive && index === 0)) {
                now = now.subtract(1, 'year');
            }

            return now.year();
        });
};

export const monthOptions = [
    { label: 'All months', value: 'all' },
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
];

export const getYearsOptions = () => {
    const today = dayjs.utc();
    const currentYear = today.year();
    const month = today.month();
    const isEndOfYear = month === 11;
    const yearsOptions: { label: string; value: string | number }[] = [
        {
            label: 'All Years',
            value: 'all',
        },
    ];

    if (isEndOfYear) {
        yearsOptions.push({
            label: (currentYear + 1).toString(),
            value: currentYear + 1,
        });
    }

    yearsOptions.push(...getLastXYears(null, true).map((y) => ({ label: y.toString(), value: y })));

    return yearsOptions;
};

export const yearOptions = getYearsOptions();

export const getDocumentTypeLabel = (documentType: string, documentTypes: DocumentType[]) =>
    documentTypes.find((type) => type.id === documentType)?.label ?? documentType;

export const getDocumentRelevantToField = (
    _: unknown,
    __: unknown,
    document: Pick<Document, 'year' | 'month' | 'quarter'>
) => {
    const { year, month, quarter } = document;
    const label = [];
    if (month) {
        label.push(monthOptions.find((option) => month === option.value)?.label ?? month);
    }
    if (quarter) {
        label.push(`Q${quarter}`);
    }
    if (year) {
        label.push(year);
    }
    return label.length < 1 ? 'All time' : label.join(' ');
};

export const parseDocumentFields = (document: Document) => ({
    ...document,
    quarter: document.quarter ? [`Q${document.quarter}`] : [],
    relevantToTab: document.quarter ? 1 : 0,
    file_name: document.file_name.endsWith(`.${document.file_format}`)
        ? document.file_name.split('.').slice(0, -1).join('.')
        : document.file_name,
});

/**
 * The inverse of the options above: `'all'` and `[]` mean "no selection"; consumers want nulls.
 *
 * Every caller must agree — a member request's identity is a server-side digest of its whole context,
 * so normalizing differently gives one ask two identities and the member is asked twice.
 */
export const normalizePeriod = (values: Pick<DocumentRequest, 'year' | 'month' | 'quarter'>) => ({
    year: values.year === 'all' || values.year === undefined ? null : Number(values.year),
    month: values.month === 'all' || values.month === undefined ? null : Number(values.month),
    quarter: !values.quarter?.length ? null : Number(String(values.quarter[0]).replaceAll('Q', '')),
});

/**
 * The `document` block of a `POST hub/tasks/member-requests/` body. `notes` is not trimmed: it feeds
 * the digest, so trimming would re-identify asks already recorded.
 */
export const toDocumentRequestDetails = (values: DocumentRequest) => ({
    document_type: values.document_type,
    notes: values.notes ?? '',
    is_internal: values.is_internal,
    ...normalizePeriod(values),
});

export const sortDocumentTypes = (documentTypes: DocumentType[]) => {
    const mostUploadedTypes = documentTypes
        .filter((type) => MOST_UPLOADED_DOCUMENT_TYPES.includes(type.id))
        .sort((a, b) => MOST_UPLOADED_DOCUMENT_TYPES.indexOf(a.id) - MOST_UPLOADED_DOCUMENT_TYPES.indexOf(b.id))
        .map((type) => ({ ...type, mostPopular: true }));

    return [...mostUploadedTypes, ...documentTypes];
};
